import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLock, faCrown, faCheckCircle, faRocket, faInfinity,
  faChartBar, faGraduationCap, faArrowRight, faTimesCircle
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { getExamUrl } from '../utils/slugify';
import api from '../config/api';

interface UpgradeWallProps {
  examId: number;
  examName: string;
  reason: 'exam_mode' | 'no_free_tier' | 'exhausted_free' | 'premium_only';
  onClose?: () => void;
  freeModeEnabled?: boolean;
}

interface PricingPlan {
  period_days: number;
  price_cents: number;
  currency: string;
  is_active: boolean;
}

const UpgradeWall: React.FC<UpgradeWallProps> = ({ examId, examName, reason, onClose, freeModeEnabled = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pricingPlans, setPricingPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);

  console.log('UpgradeWall rendering:', { examId, examName, reason, user: !!user, freeModeEnabled });

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await api.get('/subscriptions/pricing');
      setPricingPlans(response.data.pricing || []);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (priceCents: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(priceCents / 100);
  };

  const getPlanLabel = (days: number) => {
    if (days === 365) return '1 Year';
    if (days === 60) return '2 Months';
    if (days === 90) return '3 Months';
    return `${days} Days`;
  };

  // Get the 1-year plan (365 days) as the featured plan, or the longest available plan
  const featuredPlan = pricingPlans.find(p => p.period_days === 365) || 
                       pricingPlans.sort((a, b) => b.period_days - a.period_days)[0];

  const reasons = {
    exam_mode: {
      title: freeModeEnabled ? 'Sign Up for Unlimited Free Access' : 'Exam Mode Requires Premium Access',
      message: freeModeEnabled 
        ? 'Unlock unlimited access for free! Create a free account to access all questions, exam mode, and features at no cost.'
        : (user ? 'Unlock Exam Mode to simulate real exam conditions with timers and comprehensive results.' : 'Sign up to get 20 questions in practice mode. Subscribe to unlock exam mode and unlimited questions.'),
      icon: freeModeEnabled ? faRocket : faGraduationCap
    },
    no_free_tier: {
      title: freeModeEnabled ? 'Sign Up for Unlimited Free Access' : 'Premium Exam Access Required',
      message: freeModeEnabled
        ? 'Unlock unlimited access for free! Create a free account to access all questions and exam features completely free.'
        : (user ? 'This exam requires a premium subscription. Get full access to all questions and features.' : 'Sign up to get 20 questions in practice mode. Subscribe to unlock exam mode and unlimited questions.'),
      icon: freeModeEnabled ? faRocket : faLock
    },
    exhausted_free: {
      title: freeModeEnabled ? 'Sign Up for Unlimited Free Questions' : 'You\'ve Completed All Free Questions',
      message: freeModeEnabled
        ? 'Unlock unlimited access for free! Create a free account to access all questions, exam mode, and features at no cost.'
        : (user ? 'Great progress! Upgrade to access unlimited questions and exam mode.' : 'Sign up to get 20 questions in practice mode. Subscribe to unlock exam mode and unlimited questions.'),
      icon: freeModeEnabled ? faRocket : faCheckCircle
    },
    premium_only: {
      title: freeModeEnabled ? 'Sign Up for Unlimited Free Access' : 'Premium Content',
      message: freeModeEnabled
        ? 'Unlock unlimited access for free! Create a free account to access all questions and features completely free.'
        : (user ? 'This content requires a premium subscription for full access.' : 'Sign up to get 20 questions in practice mode. Subscribe to unlock exam mode and unlimited questions.'),
      icon: freeModeEnabled ? faRocket : faCrown
    }
  };

  const currentReason = reasons[reason];

  const handleUpgrade = () => {
    if (!user) {
      navigate('/register', { state: { examId: examId } });
    } else {
      navigate(`/subscription/${examId}`);
    }
  };

  return (
    <div className="gp-modal-backdrop">
      <div className="gp-modal max-w-xl">
        {/* Header — brand teal gradient */}
        <div
          className="relative text-white p-4 sm:p-5 rounded-t-[var(--gp-radius-lg)]"
          style={{ background: 'linear-gradient(135deg, var(--gp-brand) 0%, #17838d 100%)' }}
        >
          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-3 right-3 text-white/80 hover:text-white transition-colors"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimesCircle} className="text-xl" />
            </button>
          )}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-md flex items-center justify-center shrink-0">
              <FontAwesomeIcon icon={currentReason.icon} className="text-xl" />
            </div>
            <div>
              <h2 className="font-display text-lg sm:text-xl font-semibold leading-tight">{currentReason.title}</h2>
              <p className="text-sm mt-0.5 opacity-90">{examName}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5">
          <p className="gp-text-muted mb-4 text-sm leading-relaxed">
            {currentReason.message}
          </p>

          {/* Features */}
          <div
            className="rounded-md p-4 mb-4"
            style={{
              background: freeModeEnabled ? 'var(--gp-success-soft)' : 'var(--gp-surface-muted)',
              border: `1px solid ${freeModeEnabled ? 'var(--gp-success)' : 'var(--gp-border)'}`,
            }}
          >
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2" style={{ color: 'var(--gp-ink)' }}>
              <FontAwesomeIcon
                icon={freeModeEnabled ? faRocket : faCrown}
                style={{ color: freeModeEnabled ? 'var(--gp-success)' : 'var(--gp-accent)' }}
              />
              {freeModeEnabled ? 'What You\'ll Get (100% Free!):' : 'What You\'ll Get:'}
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="flex items-start gap-2.5">
                <FontAwesomeIcon
                  icon={faInfinity}
                  className="mt-0.5 text-sm"
                  style={{ color: freeModeEnabled ? 'var(--gp-success)' : 'var(--gp-brand)' }}
                />
                <div>
                  <p className="font-semibold text-sm" style={{ color: 'var(--gp-ink)' }}>
                    {freeModeEnabled ? 'Unlimited Free Questions' : 'Unlimited Questions'}
                  </p>
                  <p className="text-xs gp-text-muted">
                    {freeModeEnabled ? 'Access to ALL questions - completely free!' : 'Access to all premium questions'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <FontAwesomeIcon
                  icon={faGraduationCap}
                  className="mt-0.5 text-sm"
                  style={{ color: freeModeEnabled ? 'var(--gp-success)' : 'var(--gp-brand)' }}
                />
                <div>
                  <p className="font-semibold text-sm" style={{ color: 'var(--gp-ink)' }}>Exam Mode</p>
                  <p className="text-xs gp-text-muted">
                    {freeModeEnabled ? 'Full exam simulation - free!' : 'Simulated exam experience'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <FontAwesomeIcon
                  icon={faChartBar}
                  className="mt-0.5 text-sm"
                  style={{ color: freeModeEnabled ? 'var(--gp-success)' : 'var(--gp-success)' }}
                />
                <div>
                  <p className="font-semibold text-sm" style={{ color: 'var(--gp-ink)' }}>Detailed Analytics</p>
                  <p className="text-xs gp-text-muted">
                    {freeModeEnabled ? 'Track progress - completely free!' : 'Track your progress'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <FontAwesomeIcon
                  icon={faRocket}
                  className="mt-0.5 text-sm"
                  style={{ color: freeModeEnabled ? 'var(--gp-success)' : 'var(--gp-accent)' }}
                />
                <div>
                  <p className="font-semibold text-sm" style={{ color: 'var(--gp-ink)' }}>
                    {freeModeEnabled ? '100% Free Access' : (featuredPlan ? `${getPlanLabel(featuredPlan.period_days)} Access` : 'Flexible Access')}
                  </p>
                  <p className="text-xs gp-text-muted">
                    {freeModeEnabled ? 'No payment required - ever!' : (featuredPlan ? `Full access for ${featuredPlan.period_days} days` : 'Choose your subscription period')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing - Only show if not in free mode */}
          {!freeModeEnabled && (
            <>
              {loading ? (
                <div className="gp-soft-panel p-4 mb-4 text-center">
                  <div className="gp-spinner mb-2" role="status" aria-label="Loading pricing" />
                  <p className="gp-text-muted text-sm">Loading pricing...</p>
                </div>
              ) : featuredPlan ? (
                <div className="gp-soft-panel p-4 mb-4 text-center">
                  {featuredPlan.period_days === 365 && (
                    <div
                      className="inline-block px-2.5 py-0.5 rounded text-xs font-semibold mb-3"
                      style={{ background: 'var(--gp-success-soft)', color: 'var(--gp-success)' }}
                    >
                      Best Value
                    </div>
                  )}
                  <div className="mb-2">
                    <span className="font-display text-3xl font-bold" style={{ color: 'var(--gp-ink)' }}>
                      {formatPrice(featuredPlan.price_cents, featuredPlan.currency)}
                    </span>
                    <span className="gp-text-muted ml-1.5 text-sm">
                      / {getPlanLabel(featuredPlan.period_days).toLowerCase()}
                    </span>
                  </div>
                  <p className="gp-text-muted text-sm mb-2">
                    Full access to {examName} for {getPlanLabel(featuredPlan.period_days).toLowerCase()}
                  </p>
                  {pricingPlans.length > 1 && (
                    <p className="text-xs" style={{ color: 'var(--gp-ink-subtle)' }}>
                      View all plans on subscription page
                    </p>
                  )}
                </div>
              ) : (
                <div className="gp-soft-panel p-4 mb-4 text-center">
                  <p className="gp-text-muted text-sm">
                    Pricing information will be available on the subscription page
                  </p>
                </div>
              )}
            </>
          )}
          
          {/* Free Mode Banner */}
          {freeModeEnabled && (
            <div
              className="rounded-md p-4 mb-4 text-center"
              style={{
                background: 'var(--gp-success-soft)',
                border: '1px solid var(--gp-success)',
              }}
            >
              <div
                className="inline-block px-3 py-1 rounded text-sm font-bold mb-3"
                style={{ background: 'var(--gp-success)', color: '#fff' }}
              >
                100% FREE - No Payment Required!
              </div>
              <div className="mb-2">
                <span className="font-display text-4xl font-bold" style={{ color: 'var(--gp-success)' }}>
                  FREE
                </span>
              </div>
              <p className="text-sm font-semibold mb-1" style={{ color: 'var(--gp-ink)' }}>
                Unlimited access to all questions and features
              </p>
              <p className="text-sm gp-text-muted">
                Sign up now to get instant access to {examName} - completely free!
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2.5">
            <button
              onClick={handleUpgrade}
              className={`flex-1 ${freeModeEnabled ? 'gp-btn-accent' : 'gp-btn-primary'}`}
            >
              <FontAwesomeIcon icon={freeModeEnabled ? faRocket : faCrown} />
              <span>
                {freeModeEnabled
                  ? (user ? 'Get Unlimited Free Access' : 'Sign Up for Unlimited Free Access')
                  : (user ? 'Subscribe to Unlock More' : 'Sign Up')
                }
              </span>
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
            {onClose && (
              <button onClick={onClose} className="gp-btn-ghost">
                Maybe Later
              </button>
            )}
          </div>

          {!user && (
            <p className="text-center text-sm gp-text-muted mt-3">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login', { state: { examId: examId } })}
                className="font-semibold hover:underline"
                style={{ color: 'var(--gp-brand)' }}
              >
                Sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpgradeWall;
